import { supabase } from '@/integrations/supabase/client';
import {
  ensureSupabaseAuthSession,
  readNativeAuthSessionState,
} from '@/lib/mobile/auth-persistence';
import { settleByDeadline } from '@/lib/mobile/auth-bootstrap-deadline';
import {
  acknowledgeNativeAuthCallbackOutcome,
  beginNativeAuthCallback,
  claimNativeOAuthCallbackUrl,
  completeNativeAuthCallback,
  getNativeAuthCallbackPreviousIdentity,
} from '@/lib/mobile/native-auth-launch';
import { runWhenNativeAuthSubscriptionReady } from '@/lib/mobile/native-auth-subscription-readiness';

import type { NavigateFunction } from 'react-router-dom';

export const NATIVE_OAUTH_FINISHED_EVENT = 'public-pulse:native-oauth-finished';

const AUTH_LOGIN_PATH = '/auth/login';
const PRE_CALLBACK_SESSION_DEADLINE_MS = 1000;
const CALLBACK_CLEANUP_DEADLINE_MS = 250;

export interface OAuthMessages {
  readonly failedTitle: string;
  readonly failedDescription: string;
  readonly cancelledTitle: string;
  readonly cancelledDescription: string;
}

type OAuthToast = (input: { title: string; description: string; variant: 'destructive' }) => void;

interface OAuthCallbackWork {
  readonly url: string;
  readonly navigate: NavigateFunction;
  readonly toast: OAuthToast;
  readonly messages: OAuthMessages;
  readonly shouldPublishUi: () => boolean;
}

type OAuthErrorReason = 'cancelled' | 'failed';

let nativeOAuthCallbackQueue = Promise.resolve();

function ignoreNativeOAuthCallbackFailure(): undefined {
  return undefined;
}

function getSearchParams(url: string): URLSearchParams | null {
  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) {
    return null;
  }

  const hashIndex = url.indexOf('#', queryIndex);
  const query = url.slice(queryIndex + 1, hashIndex === -1 ? undefined : hashIndex);
  return new URLSearchParams(query);
}

function getOAuthErrorReason(url: string): OAuthErrorReason | null {
  const searchError = getSearchParams(url)?.get('error');
  const hashIndex = url.indexOf('#');
  const hashError =
    hashIndex === -1
      ? null
      : new URLSearchParams(url.slice(Math.max(0, hashIndex + 1))).get('error');
  const error = searchError ?? hashError;
  if (!error) {
    return null;
  }

  return error === 'access_denied' ? 'cancelled' : 'failed';
}

async function completeCallbackOutcome(
  outcome: Parameters<typeof completeNativeAuthCallback>[0],
): Promise<void> {
  const result = await settleByDeadline(
    completeNativeAuthCallback(outcome),
    CALLBACK_CLEANUP_DEADLINE_MS,
    new AbortController().signal,
  );
  if (result.status === 'failed' || result.status === 'timed-out') {
    console.error('[DeepLinkAuth] PKCE verifier cleanup failed');
  }
}

async function readPreCallbackSession() {
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  try {
    return await Promise.race([
      readNativeAuthSessionState(),
      new Promise<Awaited<ReturnType<typeof readNativeAuthSessionState>>>((resolve) => {
        timeoutId = globalThis.setTimeout(
          () => resolve({ status: 'unavailable' }),
          PRE_CALLBACK_SESSION_DEADLINE_MS,
        );
      }),
    ]);
  } catch {
    return { status: 'unavailable' } as const;
  } finally {
    if (timeoutId !== undefined) {
      globalThis.clearTimeout(timeoutId);
    }
  }
}

async function closeOAuthBrowser(): Promise<void> {
  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.close().catch(() => null);
  } catch {
    // Callback recovery must continue when the browser is already closed.
  }
  globalThis.window.dispatchEvent(new Event(NATIVE_OAUTH_FINISHED_EVENT));
}

async function finishOAuthCallback(work: OAuthCallbackWork): Promise<void> {
  await closeOAuthBrowser();
  if (work.shouldPublishUi()) {
    Promise.resolve(work.navigate('/auth/callback', { replace: true })).catch(() => null);
  }
}

async function notifyOAuthFailure(
  work: OAuthCallbackWork,
  title: string,
  description: string,
): Promise<boolean> {
  await closeOAuthBrowser();
  if (!work.shouldPublishUi()) {
    return false;
  }
  work.toast({ title, description, variant: 'destructive' });
  Promise.resolve(work.navigate(AUTH_LOGIN_PATH, { replace: true })).catch(() => null);
  return true;
}

async function recoverFailedOAuthCallback(
  work: OAuthCallbackWork,
  title: string,
  description: string,
): Promise<void> {
  const outcome = { status: 'failure' } as const;
  await completeCallbackOutcome(outcome);
  if (await notifyOAuthFailure(work, title, description)) {
    runWhenNativeAuthSubscriptionReady(() => acknowledgeNativeAuthCallbackOutcome(outcome));
  }
}

async function failOAuthCallback(work: OAuthCallbackWork, reason: string): Promise<void> {
  console.error(`[DeepLinkAuth] ${reason}`);
  await recoverFailedOAuthCallback(
    work,
    work.messages.failedTitle,
    work.messages.failedDescription,
  );
}

async function handlePkceSession(work: OAuthCallbackWork): Promise<boolean> {
  const code = getSearchParams(work.url)?.get('code');
  if (!code) {
    return false;
  }

  const previousSession = await readPreCallbackSession();
  const { data, error } = await supabase.auth.exchangeCodeForSession(code);
  if (error) {
    await failOAuthCallback(work, 'Code exchange failed');
    return true;
  }

  const sessionReady = await ensureSupabaseAuthSession(supabase, data?.session);
  if (!sessionReady || !data?.session) {
    await failOAuthCallback(work, 'Session hydration failed');
    return true;
  }

  await completeCallbackOutcome({
    status: 'success',
    session: data.session,
    previousIdentity: getNativeAuthCallbackPreviousIdentity(previousSession),
  });
  await finishOAuthCallback(work);
  return true;
}

async function processOAuthCallback(work: OAuthCallbackWork): Promise<void> {
  try {
    if (!beginNativeAuthCallback()) {
      console.error('[DeepLinkAuth] Previous auth callback is incomplete');
      await notifyOAuthFailure(work, work.messages.failedTitle, work.messages.failedDescription);
      return;
    }

    const oauthErrorReason = getOAuthErrorReason(work.url);
    if (oauthErrorReason) {
      const cancelled = oauthErrorReason === 'cancelled';
      await recoverFailedOAuthCallback(
        work,
        cancelled ? work.messages.cancelledTitle : work.messages.failedTitle,
        cancelled ? work.messages.cancelledDescription : work.messages.failedDescription,
      );
      return;
    }

    const handledPkce = await handlePkceSession(work);
    if (!handledPkce) {
      console.warn('[DeepLinkAuth] OAuth callback missing credentials');
      await recoverFailedOAuthCallback(
        work,
        work.messages.failedTitle,
        work.messages.failedDescription,
      );
    }
  } catch {
    console.error('[DeepLinkAuth] OAuth callback handling failed');
    await recoverFailedOAuthCallback(
      work,
      work.messages.failedTitle,
      work.messages.failedDescription,
    );
  }
}

export function queueNativeOAuthCallback(work: OAuthCallbackWork): void {
  const queued = nativeOAuthCallbackQueue.then(async () => {
    if (!claimNativeOAuthCallbackUrl(work.url)) {
      return;
    }
    await processOAuthCallback(work);
  });
  nativeOAuthCallbackQueue = queued.catch(ignoreNativeOAuthCallbackFailure);
}

export function __resetNativeOAuthCallbackQueueForTests(): void {
  nativeOAuthCallbackQueue = Promise.resolve();
}
